import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';

interface Match {
  id: number;
  team1_name: string;
  team2_name: string;
  team1_id: number;
  team2_id: number;
  match_date: string;
  venue: string;
  status: string;
  result?: string;
  tournament_name: string;
  tournament_id: number;
  format: string;
}

const LiveMatches: React.FC = () => {
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'live' | 'upcoming'>('live');
  const [filterTournament, setFilterTournament] = useState<string>('all');
  const [tournaments, setTournaments] = useState<{ id: number; name: string }[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchMatches();
    fetchTournaments();
  }, []);

  const fetchMatches = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await apiService.getAllMatches();

      if (data.success) {
        setMatches(data.matches);
      } else {
        setError(data.message || 'Failed to load matches');
      }
    } catch (err: any) {
      setError('Failed to load matches');
      console.error('Error fetching matches:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTournaments = async () => {
    try {
      const data = await apiService.getLiveTournaments();

      if (data.success) {
        setTournaments(data.liveTournaments || []);
      }
    } catch (err) {
      console.error('Error fetching tournaments:', err);
    }
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'live':
        return { label: 'LIVE', color: 'bg-red-500 text-white', icon: '🔴' };
      case 'upcoming':
        return { label: 'Upcoming', color: 'bg-blue-500 text-white', icon: '⏰' };
      case 'completed':
        return { label: 'Completed', color: 'bg-gray-500 text-white', icon: '✅' };
      case 'cancelled':
        return { label: 'Cancelled', color: 'bg-gray-400 text-white', icon: '❌' };
      default:
        return { label: status, color: 'bg-gray-300 text-gray-800', icon: '🏏' };
    }
  };

  const getFormatInfo = (format: string) => {
    switch (format?.toLowerCase()) {
      case 't20':
        return { label: 'T20', color: 'bg-green-100 text-green-800' };
      case 'odi':
        return { label: 'ODI', color: 'bg-blue-100 text-blue-800' };
      case 'test':
        return { label: 'Test', color: 'bg-red-100 text-red-800' };
      case 't10':
        return { label: 'T10', color: 'bg-purple-100 text-purple-800' };
      default:
        return { label: format || 'Unknown', color: 'bg-gray-100 text-gray-800' };
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return `Today at ${date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      })}`;
    } else if (diffDays === 1) {
      return `Tomorrow at ${date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      })}`;
    } else if (diffDays > 1) {
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    }
  };

  const filteredMatches = matches.filter(match => {
    // Filter by status
    const statusMatch = activeTab === 'live' 
      ? match.status === 'live' 
      : ['upcoming', 'scheduled'].includes(match.status);
    
    // Filter by tournament
    const tournamentMatch = filterTournament === 'all' || 
      match.tournament_id === parseInt(filterTournament);
    
    return statusMatch && tournamentMatch;
  });

  const handleViewMatch = (matchId: number) => {
    navigate(`/match/${matchId}`);
  };

  const handleViewTournament = (tournamentId: number) => {
    navigate(`/tournament/${tournamentId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-400 text-xl mb-2">⚠️</div>
        <p className="text-white mb-4">{error}</p>
        <button 
          onClick={fetchMatches}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Live Matches</h1>
        <p className="text-white/60">Track live and upcoming cricket matches</p>
      </div>

      {/* Filters */}
      <div className="bg-white/10 backdrop-blur-md rounded-lg p-6 border border-white/20 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          {/* Status Tabs */}
          <div className="flex space-x-1 bg-white/10 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('live')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'live'
                  ? 'bg-red-500 text-white'
                  : 'text-white/60 hover:text-white'
              }`}
            >
              🔴 Live
            </button>
            <button
              onClick={() => setActiveTab('upcoming')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'upcoming'
                  ? 'bg-blue-500 text-white'
                  : 'text-white/60 hover:text-white'
              }`}
            >
              ⏰ Upcoming
            </button>
          </div>

          {/* Tournament Filter */}
          <div className="flex items-center space-x-3">
            <label className="text-white/80 text-sm">Tournament:</label>
            <select
              value={filterTournament}
              onChange={(e) => setFilterTournament(e.target.value)}
              className="px-3 py-2 bg-white/20 border border-white/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="all" className="text-gray-800">All Tournaments</option>
              {tournaments.map(tournament => (
                <option key={tournament.id} value={tournament.id} className="text-gray-800">
                  {tournament.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div className="flex justify-between items-center mb-6">
        <p className="text-white/80">
          Showing {filteredMatches.length} {activeTab === 'live' ? 'live' : 'upcoming'} matches
        </p>
        <button
          onClick={fetchMatches}
          className="text-blue-400 hover:text-blue-300 transition-colors text-sm"
        >
          Refresh
        </button>
      </div>

      {/* Matches List */}
      {filteredMatches.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🏏</div>
          <p className="text-xl text-white mb-2">
            No {activeTab === 'live' ? 'live' : 'upcoming'} matches found
          </p>
          <p className="text-blue-200 mb-4">
            {activeTab === 'live' 
              ? 'Check back later for live matches' 
              : 'No upcoming matches scheduled'
            }
          </p>
          <button
            onClick={() => setActiveTab(activeTab === 'live' ? 'upcoming' : 'live')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            View {activeTab === 'live' ? 'Upcoming' : 'Live'} Matches
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredMatches.map(match => {
            const statusInfo = getStatusInfo(match.status);
            const formatInfo = getFormatInfo(match.format);
            
            return (
              <div key={match.id} className="bg-white/10 backdrop-blur-md rounded-lg p-6 border border-white/20 hover:bg-white/15 transition-all duration-200">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                  {/* Left side - Match info */}
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <span className="text-2xl">{statusInfo.icon}</span>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
                            {statusInfo.label}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${formatInfo.color}`}>
                            {formatInfo.label}
                          </span>
                        </div>
                        <h3 className="text-lg font-semibold text-white">
                          {match.team1_name} vs {match.team2_name}
                        </h3>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-white/60">Tournament:</span>
                        <div className="text-white font-medium">{match.tournament_name}</div>
                      </div>
                      <div>
                        <span className="text-white/60">Date & Time:</span>
                        <div className="text-white font-medium">{formatDate(match.match_date)}</div>
                      </div>
                      <div>
                        <span className="text-white/60">Venue:</span>
                        <div className="text-white font-medium">{match.venue}</div>
                      </div>
                      {match.result && (
                        <div>
                          <span className="text-white/60">Result:</span>
                          <div className="text-white font-medium">{match.result}</div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Right side - Action buttons */}
                  <div className="flex space-x-2 lg:flex-col lg:space-x-0 lg:space-y-2">
                    <button
                      onClick={() => handleViewMatch(match.id)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    >
                      View Match
                    </button>
                    <button
                      onClick={() => handleViewTournament(match.tournament_id)}
                      className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    >
                      View Tournament
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LiveMatches; 